import { useEffect } from "react";
import { api } from "@/lib/api";
import { InfoTip } from "@/components/InfoTip";

const TIPS = {
  copies: "The number of finished copies you need of this document.",
  colour: "Mono prints in black & white only. Colour prints in full colour and costs more per page.",
  size: "A4 is standard document size (210×297mm). A3 is twice as large — poster size.",
  paper: "80gsm is standard printer paper. 250gsm is thick, rigid card stock.",
  sides: "Single-sided prints on one side of each sheet. Double-sided prints on both sides — each printed side counts as a page.",
  pickup: "Choose ASAP or pick a specific date and time you'd like to collect your printing.",
};

const OPTS = {
  color: [
    { v: "bw", label: "Mono" },
    { v: "color", label: "Colour" },
  ],
  size: [
    { v: "A4", label: "A4", sub: "Regular paper" },
    { v: "A3", label: "A3", sub: "Poster size" },
  ],
  paper: [
    { v: "80", label: "80gsm", sub: "Normal thickness" },
    { v: "250", label: "250gsm", sub: "Card stock" },
  ],
  sided: [
    { v: "single", label: "Single sided" },
    { v: "double", label: "Double sided" },
  ],
};

function Choice({ label, options, value, onChange, name, tip }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-2">
        <label className="block uppercase text-xs tracking-[0.18em] font-medium text-slate-500">{label}</label>
        {tip && <InfoTip text={tip} label={name === "color" ? "colour" : name === "sided" ? "sides" : name} />}
      </div>
      <div className="grid grid-cols-2 gap-3">
        {options.map((o) => {
          const active = value === o.v;
          return (
            <button
              key={o.v}
              type="button"
              data-testid={`opt-${name}-${o.v}`}
              onClick={() => onChange(o.v)}
              className={`text-left px-4 py-3 rounded-lg border-2 transition-colors ${
                active ? "border-brand bg-orange-50" : "border-slate-200 bg-white hover:border-slate-300"
              }`}
            >
              <div className="font-semibold text-sm text-ink">{o.label}</div>
              {o.sub && <div className="text-xs text-slate-500 mt-0.5">{o.sub}</div>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function PrintOptionsForm({ opts, setOpts, price, currency = "€", pages = null, pagesKnown = true }) {
  const set = (k) => (v) => setOpts((s) => ({ ...s, [k]: v }));

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-1.5 mb-2">
          <label className="block uppercase text-xs tracking-[0.18em] font-medium text-slate-500">How many copies?</label>
          <InfoTip text={TIPS.copies} label="copies" />
        </div>
        <input
          type="number"
          min={1}
          data-testid="opt-copies"
          value={opts.copies}
          onChange={(e) => { const v = e.target.value; set("copies")(v === "" ? "" : Math.max(1, parseInt(v, 10) || 1)); }}
          onBlur={(e) => { if (e.target.value === "" || parseInt(e.target.value, 10) < 1) set("copies")(1); }}
          className="w-32 px-4 py-2.5 rounded-lg border-2 border-slate-200 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand text-ink font-medium"
        />
      </div>
      <Choice name="color" label="Colour" tip={TIPS.colour} options={OPTS.color} value={opts.color} onChange={set("color")} />
      <Choice name="size" label="Paper size" tip={TIPS.size} options={OPTS.size} value={opts.size} onChange={set("size")} />
      <Choice name="paper" label="Paper type" tip={TIPS.paper} options={OPTS.paper} value={opts.paper} onChange={set("paper")} />
      <Choice name="sided" label="Sides" tip={TIPS.sides} options={OPTS.sided} value={opts.sided} onChange={set("sided")} />

      <div>
        <div className="flex items-center gap-1.5 mb-2">
          <label className="block uppercase text-xs tracking-[0.18em] font-medium text-slate-500">Pick-up time</label>
          <InfoTip text={TIPS.pickup} label="pickup" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            data-testid="opt-pickup-asap"
            onClick={() => setOpts((s) => ({ ...s, pickup_asap: true }))}
            className={`px-4 py-3 rounded-lg border-2 font-semibold text-sm transition-colors ${
              opts.pickup_asap ? "border-brand bg-orange-50 text-ink" : "border-slate-200 bg-white hover:border-slate-300 text-ink"
            }`}
          >
            ASAP
          </button>
          <button
            type="button"
            data-testid="opt-pickup-scheduled"
            onClick={() => setOpts((s) => ({ ...s, pickup_asap: false }))}
            className={`px-4 py-3 rounded-lg border-2 font-semibold text-sm transition-colors ${
              !opts.pickup_asap ? "border-brand bg-orange-50 text-ink" : "border-slate-200 bg-white hover:border-slate-300 text-ink"
            }`}
          >
            Schedule
          </button>
        </div>
        {!opts.pickup_asap && (
          <input
            type="datetime-local"
            data-testid="opt-pickup-datetime"
            value={opts.pickup_datetime || ""}
            onChange={(e) => setOpts((s) => ({ ...s, pickup_datetime: e.target.value }))}
            className="mt-3 w-full px-4 py-2.5 rounded-lg border-2 border-slate-200 focus:border-brand focus:outline-none text-ink"
          />
        )}
      </div>

      {!pagesKnown && (
        <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800" data-testid="pagecount-notice">
          <span className="font-semibold">Page count unavailable.</span>
          <span>We couldn't automatically read the number of pages in this file, so the price shown assumes 1 page. The final price will be confirmed by our team.</span>
        </div>
      )}
      {price !== null && price !== undefined && (
        <div className="flex items-center justify-between rounded-xl bg-ink text-white px-5 py-4" data-testid="price-display">
          <div>
            <span className="uppercase text-xs tracking-[0.18em] text-slate-300 block">Estimated price</span>
            {pagesKnown && pages ? (
              <span className="text-xs text-slate-300 mt-0.5 block">{pages} {pages > 1 ? "pages" : "page"} × {opts.copies} {opts.copies > 1 ? "copies" : "copy"}</span>
            ) : null}
          </div>
          <span className="font-heading font-extrabold text-2xl">{currency}{Number(price).toFixed(2)}</span>
        </div>
      )}
    </div>
  );
}

export function useLiveQuote(opts, setPrice, setCurrency, pages = 1) {
  useEffect(() => {
    let active = true;
    api
      .post("/quote", { copies: opts.copies, color: opts.color, size: opts.size, paper: opts.paper, sided: opts.sided, pages: pages || 1 })
      .then(({ data }) => {
        if (active) {
          setPrice(data.price);
          if (setCurrency) setCurrency(data.currency_symbol);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [opts.copies, opts.color, opts.size, opts.paper, opts.sided, pages, setPrice, setCurrency]);
}

export const DEFAULT_OPTS = {
  copies: 1,
  color: "bw",
  size: "A4",
  paper: "80",
  sided: "single",
  pickup_asap: true,
  pickup_datetime: "",
};
